/**
 * useInstagramConfig Hook
 * Mirrors useWhatsAppConfig.ts's shape for the Instagram Login connect flow.
 * App credentials are saved by a super-admin, never from the tenant side.
 */

import { useState, useEffect } from 'react';
import adminApi, { InstagramConfig } from '@/services/admin.api';

export const useInstagramConfig = () => {
  const [config, setConfig] = useState<InstagramConfig | null>(null);
  // Start in the loading state so the page's `loading && !config` guard shows
  // the spinner on the very first paint. If this began as `false`, the first
  // render (before the fetch effect fires) would fall through to the
  // "Not connected" screen and flash it for a frame before real data arrives.
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminApi.getInstagramConfig();
      setConfig(data);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        'Failed to fetch Instagram configuration';
      setError(errorMessage);
      console.error('Failed to fetch Instagram config:', err);
    } finally {
      setLoading(false);
    }
  };

  /** Kicks off the Instagram Login OAuth flow — redirects the current tab to Meta's consent screen. */
  const connectInstagram = async () => {
    try {
      setConnecting(true);
      setError('');
      const { authorizeUrl } = await adminApi.connectInstagram();
      window.location.href = authorizeUrl;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        'Failed to start the Instagram connection';
      setError(errorMessage);
      setConnecting(false);
    }
  };

  const disconnectInstagram = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      await adminApi.disconnectInstagram();
      await fetchConfig();
      setSuccess('Instagram disconnected.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        'Failed to disconnect Instagram';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return {
    config,
    loading,
    connecting,
    error,
    success,
    connectInstagram,
    disconnectInstagram,
    refetch: fetchConfig,
  };
};
